import { useId, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { registerRefetch, unregisterRefetch } from './widgetRefetchRegistry';

export interface UseWidgetDataOptions {
  /** Whether to fetch data (default: true). Set false to disable fetching. */
  enabled?: boolean;
  /** Enable auto-refresh at the given interval */
  autoRefresh?: boolean;
  /** Auto-refresh interval in seconds */
  refreshInterval?: number;
  /** Widget instance ID — if provided, registers the refetch callback so the
   *  grid titlebar can show a refresh button. */
  widgetInstanceId?: string;
}

export interface UseWidgetDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Shared hook for widget data fetching with loading/error states and auto-refresh.
 * Backed by @tanstack/react-query for caching, deduplication, and background refetches.
 *
 * @param fetchFn - Async function that returns the data. Should throw on error.
 * @param deps - Dependency array that triggers a refetch when changed.
 * @param options - Optional: enabled, autoRefresh, refreshInterval, widgetInstanceId
 */
export function useWidgetData<T>(
  fetchFn: () => Promise<T>,
  deps: unknown[],
  options?: UseWidgetDataOptions,
): UseWidgetDataResult<T> {
  // useId() ensures unique cache per hook instance, preventing collisions
  // when two widgets share the same deps but call different APIs
  const id = useId();
  const enabled = options?.enabled ?? true;

  const { data, isLoading, error, refetch } = useQuery<T>({
    queryKey: ['widget-data', id, ...deps],
    queryFn: fetchFn,
    enabled,
    refetchInterval:
      options?.autoRefresh && options?.refreshInterval
        ? options.refreshInterval * 1000
        : undefined,
  });

  const refetchFn = () => { refetch(); };

  // Register refetch callback in the global registry so the titlebar can use it
  const widgetInstanceId = options?.widgetInstanceId;
  useEffect(() => {
    if (!widgetInstanceId) return;
    registerRefetch(widgetInstanceId, refetchFn);
    return () => unregisterRefetch(widgetInstanceId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetInstanceId]);

  return {
    data: data ?? null,
    loading: isLoading,
    error: error
      ? error instanceof Error
        ? error.message
        : 'Failed to fetch data'
      : null,
    refetch: refetchFn,
  };
}
